<script setup lang="ts">
import { useFormKitContextById } from "@formkit/vue";
import type { EventDetails, MaxBid, ProductDetails } from "mws-ts-v2-sdk";
import { getFormattedCurrencyAmount } from "mws-utils/lib";

import { BidAttemptType } from "~/types/Catalog";
import { getPriceInCurrency } from "~/utils/priceUtils";

type Props = {
  product: ProductDetails;
  event: EventDetails;
  activeMaxBid?: MaxBid | null;
  disabled: boolean;
};

const { product, event, activeMaxBid, disabled } = defineProps<Props>();

type Emits = {
  (e: "update-disabled-state", disabledState: boolean): void;
};

const emit = defineEmits<Emits>();

const {
  activeCurrency,
  locale,
  fullBidAmount,
  bidAmountInCents,
  getRoundedValueInCents,
  minimumBidValueInActiveCurrency,
  minimumBid,
  isSubmitBidPending,
  isBiddingDisabled,
  initializeBiddingAttempt,
  bidAttemptType,
  submitBid,
  bidModalType,
  closeBidModal,
  paymentTokenId,
  paymentTokenSelected,
} = useBidding(
  {
    product: () => product,
    event: () => event,
    activeMaxBid: () => activeMaxBid ?? null,
    bidTypes: [BidAttemptType.Maxbid, BidAttemptType.Bid],
  },
  (disabledState) => emit("update-disabled-state", disabledState),
);

const bidAmount = ref<string>();

watch(
  minimumBidValueInActiveCurrency,
  (newValue) => {
    bidAmount.value = newValue.toString();
  },
  {
    immediate: true,
  },
);

const formContext = useFormKitContextById("bidAmountForm");

const submitForm = async (bidAttemptType: BidAttemptType) => {
  document.getElementById("bidValueInCurrency")!.blur();
  bidAmountInCents.value = getRoundedValueInCents(Number(bidAmount.value));

  initializeBiddingAttempt({
    bidAttemptType,
  });
};

const biddingIsDisabled = computed(
  () => isBiddingDisabled.value || disabled || !formContext?.value?.state.valid,
);
</script>

<template>
  <div class="bid-amount flex flex-col gap-1">
    <FormKit
      id="bidAmountForm"
      v-slot="{ state: { valid } }"
      type="form"
      :actions="false"
    >
      <div class="w-full flex bid-amount-input-container">
        <BaseCurrencyInput
          id="bidValueInCurrency"
          v-model="bidAmount"
          name="bidValueInCurrency"
          :valid="valid"
          data-cy="bid-amount-input"
          :validation-name="$t('product.bidAmount.yourBid')"
          :validation="`required|number|min:${minimumBidValueInActiveCurrency}`"
          :validation-messages="{
            min: $t('product.minimumBidMessage', {
              amount: getFormattedCurrencyAmount(
                locale,
                activeCurrency.isoCurrencyCode,
                getPriceInCurrency(minimumBid, activeCurrency.isoCurrencyCode) *
                  100, // getPriceInCurrency returns EURO
              ),
            }),
          }"
          :placeholder="
            product.finished
              ? $t('product.biddingEnded')
              : $t('product.placeBid')
          "
          :disabled="product.finished"
          error-behavior="live"
        />
      </div>
      <div class="flex flex-row gap-x-2 gap-y-2 mb-2">
        <BaseButton
          v-if="!activeMaxBid"
          id="bidAmountButton"
          class="flex-1"
          data-cy="bid-amount-submit"
          :color-scheme="product.allowMaxBidding ? 'secondary' : 'primary'"
          :type="product.allowMaxBidding ? 'button' : 'submit'"
          :cta="!product.allowMaxBidding"
          :disabled="biddingIsDisabled"
          :size="product.allowMaxBidding ? 'medium' : 'regular'"
          @click.prevent="() => submitForm(BidAttemptType.Bid)"
        >
          {{ $t("product.placeBid") }}
        </BaseButton>
        <BaseButton
          v-if="product.allowMaxBidding"
          :cta="true"
          class="flex-1 md:text-sm xl:text-base"
          :disabled="biddingIsDisabled"
          data-cy="max-bid-submit"
          type="submit"
          size="medium"
          @click.prevent="() => submitForm(BidAttemptType.Maxbid)"
        >
          {{
            activeMaxBid ? $t("product.raiseMaxBid") : $t("product.placeMaxBid")
          }}
        </BaseButton>
      </div>
    </FormKit>

    <ProductBidCosts
      v-if="bidAmount !== undefined"
      :product="product"
      :event="event"
      :bid-amount="bidAmount"
    />

    <ProductBidModal
      v-if="bidModalType"
      :type="bidModalType"
      :active-max-bid="activeMaxBid"
      :product="product"
      :bid-attempt="{
        type: bidAttemptType,
        loading: isSubmitBidPending,
        amount: fullBidAmount,
      }"
      :payment-token-id="paymentTokenId"
      @confirm="submitBid"
      @close="closeBidModal"
      @continue-with-payment-token="paymentTokenSelected"
    />
  </div>
</template>

<style lang="postcss" scoped>
.bid-amount .formkit-form {
  @apply gap-y-2;
}
</style>
